import { Clock3Icon } from 'lucide-react'
import { type ComponentProps, useId, useRef, useState } from 'react'
import { FieldContext } from '../lib/field-context'
import { Button } from './button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog'
import { Field } from './field'
import { Input } from './input'
import { InputAddon, InputGroup } from './input-group'

const timePattern = '([01][0-9]|2[0-3]):[0-5][0-9]'
const pad = (value: number) => String(value).padStart(2, '0')
type TimePickerProps = Omit<
  ComponentProps<typeof Input>,
  'type' | 'value' | 'defaultValue' | 'onChange'
> & {
  value: string
  onValueChange: (value: string) => void
  labels: {
    open: string
    title: string
    close: string
    hours: string
    minutes: string
    clear: string
    confirm: string
    format: string
  }
}
export function TimePicker({
  value,
  onValueChange,
  labels,
  disabled,
  readOnly,
  ...props
}: TimePickerProps) {
  const [open, setOpen] = useState(false)
  const [hour, setHour] = useState('00')
  const [minute, setMinute] = useState('00')
  const hourRef = useRef<HTMLInputElement>(null)
  const inputTriggerRef = useRef<HTMLInputElement | null>(null)
  const hintId = useId()
  const dialogId = useId()
  const valid =
    /^\d{1,2}$/.test(hour) && /^\d{1,2}$/.test(minute) && Number(hour) < 24 && Number(minute) < 60
  function choose(next: string) {
    onValueChange(next)
    setOpen(false)
  }
  function changeOpen(next: boolean) {
    if (next && (disabled || readOnly)) return
    if (next) {
      const parts = new RegExp(`^${timePattern}$`).test(value) ? value.split(':') : ['00', '00']
      setHour(parts[0] ?? '00')
      setMinute(parts[1] ?? '00')
    }
    setOpen(next)
  }
  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <InputGroup>
        <Input
          {...props}
          disabled={disabled}
          readOnly={readOnly}
          type="text"
          inputMode="text"
          placeholder={props.placeholder ?? labels.open}
          pattern={timePattern}
          maxLength={5}
          value={value}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? dialogId : undefined}
          onClick={(event) => {
            props.onClick?.(event)
            if (event.defaultPrevented) return
            inputTriggerRef.current = event.currentTarget
            changeOpen(true)
          }}
          onKeyDown={(event) => {
            props.onKeyDown?.(event)
            if (!event.defaultPrevented && event.altKey && event.key === 'ArrowDown') {
              event.preventDefault()
              inputTriggerRef.current = event.currentTarget
              changeOpen(true)
            }
          }}
          onChange={(event) => onValueChange(event.target.value)}
        />
        <InputAddon side="end">
          <DialogTrigger asChild>
            <Button
              appearance="ghost"
              tone="neutral"
              size="icon"
              aria-label={labels.open}
              disabled={disabled || readOnly}
              onClick={() => {
                inputTriggerRef.current = null
              }}
            >
              <Clock3Icon aria-hidden="true" className="size-4" />
            </Button>
          </DialogTrigger>
        </InputAddon>
      </InputGroup>
      <FieldContext.Provider value={null}>
        <DialogContent
          id={dialogId}
          width="compact"
          className="max-w-sm"
          closeLabel={labels.close}
          aria-describedby={hintId}
          onCloseAutoFocus={(event) => {
            if (inputTriggerRef.current) {
              event.preventDefault()
              inputTriggerRef.current.focus()
              inputTriggerRef.current = null
            }
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            hourRef.current?.focus()
            hourRef.current?.select()
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
              event.preventDefault()
              if (valid) choose(`${pad(Number(hour))}:${pad(Number(minute))}`)
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{labels.title}</DialogTitle>
            <p id={hintId} className="text-sm text-muted-foreground">
              {labels.format}
            </p>
          </DialogHeader>
          <DialogBody className="space-y-5">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-2xl bg-panel-accent p-4">
              <Field label={labels.hours}>
                <Input
                  ref={hourRef}
                  aria-label={labels.hours}
                  type="number"
                  min={0}
                  max={23}
                  step={1}
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  className="text-center text-2xl tabular-nums"
                />
              </Field>
              <span aria-hidden="true" className="pt-6 text-2xl text-action">
                :
              </span>
              <Field label={labels.minutes}>
                <Input
                  aria-label={labels.minutes}
                  type="number"
                  min={0}
                  max={59}
                  step={1}
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                  className="text-center text-2xl tabular-nums"
                />
              </Field>
            </div>
          </DialogBody>
          <DialogFooter className="flex justify-between gap-3">
            <Button appearance="ghost" tone="neutral" onClick={() => choose('')}>
              {labels.clear}
            </Button>
            <Button
              disabled={!valid}
              onClick={() => choose(`${pad(Number(hour))}:${pad(Number(minute))}`)}
            >
              {labels.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </FieldContext.Provider>
    </Dialog>
  )
}
